import type { Canvas } from 'fabric'
import { useEffect, useRef, useState } from 'react'
import { useThrottledCallback } from 'use-debounce'

const HISTORY_LIMIT = 50
const SAVE_THROTTLE_MS = 400

export const useCanvasHistory = ({ canvas }: { canvas: Canvas | null }) => {
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const undoStack = useRef<any[]>([])
  const redoStack = useRef<any[]>([])
  const isRestoring = useRef(false)

  const throttledSave = useRef(
    useThrottledCallback(() => {
      saveState()
    }, SAVE_THROTTLE_MS)
  ).current

  useEffect(() => {
    if (!canvas) return

    const save = () => {
      if (!isRestoring.current) throttledSave()
    }

    canvas.on('object:modified', save)
    canvas.on('object:added', save)
    canvas.on('object:removed', save)
    canvas.on('text:changed', save)
    canvas.on('text:editing:exited', save)

    // Save initial state
    undoStack.current = [canvas.toDatalessJSON()]
    redoStack.current = []
    updateControls()

    const handleKeydown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey
      const isZ = e.key.toLowerCase() === 'z'

      if (isCtrlOrCmd && isZ) {
        e.preventDefault()
        if (e.shiftKey) {
          handleRedo()
        } else {
          handleUndo()
        }
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => {
      window.removeEventListener('keydown', handleKeydown)
      canvas.off('object:modified', save)
      canvas.off('object:added', save)
      canvas.off('object:removed', save)
      canvas.off('text:changed', save)
      canvas.off('text:editing:exited', save)
    }
  }, [canvas])

  const updateControls = () => {
    setCanUndo(undoStack.current.length > 1)
    setCanRedo(redoStack.current.length > 0)
  }

  const saveState = () => {
    if (!canvas) return
    const json = canvas.toDatalessJSON()
    const last = undoStack.current[undoStack.current.length - 1]

    // prevent duplicate saves
    if (JSON.stringify(json) === JSON.stringify(last)) return

    undoStack.current.push(json)
    if (undoStack.current.length > HISTORY_LIMIT) {
      undoStack.current.shift()
    }

    redoStack.current = []
    updateControls()
  }

  const handleUndo = async () => {
    if (!canvas || undoStack.current.length < 2) return

    const current = undoStack.current.pop()!
    redoStack.current.push(current)

    const prev = undoStack.current[undoStack.current.length - 1]
    isRestoring.current = true
    await canvas.loadFromJSON(prev)
    canvas.renderAll()
    isRestoring.current = false

    updateControls()
  }

  const handleRedo = async () => {
    if (!canvas || redoStack.current.length === 0) return

    const next = redoStack.current.pop()!
    undoStack.current.push(next)

    isRestoring.current = true
    await canvas.loadFromJSON(next)
    canvas.renderAll()
    isRestoring.current = false

    updateControls()
  }

  return {
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    saveState
  }
}
